import React from 'react';
import { CheckCircle2 } from 'lucide-react';

import { W2_CONTRACT_TID } from '../constants/testids';
import { formatDisplayDate } from '../format';

interface ContractSignedProps {
  companyName: string;
  signerName?: string;
  signedAt?: string;
}

/**
 * Signed confirmation — rendered right after a successful sign AND whenever the
 * token resolves to `Signed`.
 *
 * Confirmation ONLY: signing consumes the link, so this page never shows the terms again and
 * offers no download. The executed contract carries the member's federal tax ID, and an
 * emailed bearer URL is the wrong place to serve that — the employer receives their copy as
 * an attachment when they sign, and their OneHealth representative can re-send it.
 */
export const ContractSigned: React.FC<ContractSignedProps> = ({
  companyName,
  signerName,
  signedAt,
}) => {
  const signedDate = formatDisplayDate(signedAt);

  return (
    <div
      data-testid={W2_CONTRACT_TID.signed.root}
      className="flex flex-col items-center gap-4 py-6 text-center"
    >
      <CheckCircle2 className="size-14 text-[#2e7d4f]" aria-hidden="true" />
      <h1 className="text-3xl font-extrabold text-[#1a1a2e] max-sm:text-2xl">
        Contract signed
      </h1>
      <p
        data-testid={W2_CONTRACT_TID.signed.message}
        className="max-w-[480px] text-[15px] leading-6 text-[#5a5a70]"
      >
        The group contract for <strong>{companyName}</strong> has been signed
        {signerName && (
          <>
            {' '}
            by <strong>{signerName}</strong>
          </>
        )}
        {signedDate && <> on {signedDate}</>}. A copy has been emailed to your
        company for your records — nothing more to do here. Your OneHealth Plus
        representative will be in touch with next steps.
      </p>
    </div>
  );
};
